/**
 * RenderJobIndicator
 * ---------------------------------------------------------------
 * Tiny live status pill for an asset row in the Generated Assets
 * Queue. Subscribes to render_jobs realtime updates for one asset
 * and surfaces phase + progress without polling.
 */

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Film, CheckCircle2, AlertTriangle, UploadCloud } from "lucide-react";
import {
  fetchLatestJobForAsset,
  RENDER_STATUS_LABEL,
  type RenderJob,
  type RenderStatus,
} from "@/lib/renderJobs";

interface Props {
  assetId: string;
}

const PHASE_ICON: Record<RenderStatus, React.ComponentType<any>> = {
  queued: Loader2,
  preparing: Loader2,
  rendering: Film,
  encoding: Film,
  uploading: UploadCloud,
  completed: CheckCircle2,
  failed: AlertTriangle,
  cancelled: AlertTriangle,
};

const PHASE_TONE: Record<RenderStatus, string> = {
  queued: "border-cyan/30 text-cyan/80 bg-cyan/5",
  preparing: "border-cyan/40 text-cyan bg-cyan/10",
  rendering: "border-cyan/50 text-cyan bg-cyan/10",
  encoding: "border-amber-400/50 text-amber-300 bg-amber-400/10",
  uploading: "border-amber-400/50 text-amber-300 bg-amber-400/10",
  completed: "border-emerald-400/50 text-emerald-300 bg-emerald-400/10",
  failed: "border-red-500/50 text-red-400 bg-red-500/10",
  cancelled: "border-muted-foreground/40 text-muted-foreground bg-muted-foreground/10",
};

export function RenderJobIndicator({ assetId }: Props) {
  const [job, setJob] = useState<RenderJob | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchLatestJobForAsset(assetId).then((j) => {
      if (!cancelled) setJob(j);
    });

    const channel = supabase
      .channel(`render_jobs:${assetId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "render_jobs", filter: `asset_id=eq.${assetId}` },
        (payload) => {
          const row = payload.new as RenderJob | undefined;
          if (!row) return;
          setJob((prev) =>
            !prev || new Date(row.created_at) >= new Date(prev.created_at) ? row : prev
          );
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [assetId]);

  if (!job) return null;

  const status = job.status as RenderStatus;
  const Icon = PHASE_ICON[status] ?? Loader2;
  const showProgress =
    status === "rendering" || status === "encoding" || status === "uploading";
  const animate = status === "queued" || status === "preparing" || status === "rendering";

  return (
    <div className="mt-2 flex items-center gap-2">
      <div
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[10px] font-mono uppercase tracking-wider ${PHASE_TONE[status]}`}
      >
        <Icon className={`w-3 h-3 ${animate ? "animate-spin" : ""}`} />
        {RENDER_STATUS_LABEL[status] ?? status}
        {showProgress && job.progress > 0 && (
          <span className="tabular-nums opacity-80">· {job.progress}%</span>
        )}
      </div>
      {showProgress && (
        <div className="flex-1 max-w-[180px] h-[3px] rounded-full bg-cyan/10 overflow-hidden">
          <div
            className="h-full bg-cyan/70 transition-[width] duration-500"
            style={{ width: `${Math.max(2, job.progress)}%` }}
          />
        </div>
      )}
      {status === "failed" && job.error_log && (
        <span
          className="text-[10px] font-mono text-red-400/80 truncate max-w-[260px]"
          title={job.error_log}
        >
          {job.error_log.split("\n")[0]}
        </span>
      )}
    </div>
  );
}
